import { memo, useCallback, useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import EllipsisTooltip from './EllipsisTooltip';
import {
  ALL_OPTION_VALUE,
  DROPDOWN_ACTIVE_COLOR,
  buildOptionLabelMap,
  isAllSelected,
  isOptionSelected,
  resolveMultiSelectValue,
} from '../../utils/dropdownSelectUtils';

const DEFAULT_CONTROL_SX = { minWidth: 130, maxWidth: 130 };

/** Above MUI Select menu (1300) and app overlays (~10002) */
const DROPDOWN_TOOLTIP_Z_INDEX = 999999;

const dropdownTooltipProps = {
  PopperProps: {
    sx: { zIndex: DROPDOWN_TOOLTIP_Z_INDEX },
    style: { zIndex: DROPDOWN_TOOLTIP_Z_INDEX },
  },
  slotProps: {
    popper: {
      sx: { zIndex: DROPDOWN_TOOLTIP_Z_INDEX },
    },
  },
};

const checkboxSx = {
  padding: 0,
  marginRight: 1,
  color: '#858D9D',
  flexShrink: 0,
  pointerEvents: 'none',
  '&.Mui-checked': {
    color: DROPDOWN_ACTIVE_COLOR,
  },
  '& .MuiSvgIcon-root': {
    borderRadius: 10,
    fontSize: 20,
  },
};

const menuItemLabelSx = {
  minWidth: 0,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
};

const selectDisplaySx = {
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
};

const getMenuPaperSx = paperWidth => ({
  maxHeight: 250,
  ...(paperWidth != null
    ? { width: paperWidth, minWidth: paperWidth, maxWidth: paperWidth }
    : {}),
  zIndex: 1300,
  '& .MuiMenuItem-root': {
    paddingLeft: '8px',
    paddingRight: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiMenuItem-root.Mui-selected': {
    color: DROPDOWN_ACTIVE_COLOR,
    backgroundColor: 'transparent',
  },
  '& .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: 'rgba(29, 144, 245, 0.08)',
  },
});

const CommonDropdown = memo(
  ({
    label,
    filterOptions = [],
    value,
    handleChange,
    multiple = false,
    isAll = false,
    allLabel = '',
    emptyLabel = '',
    size = 'small',
    disabled = false,
    error = false,
    helperText = '',
    required = false,
    sx = DEFAULT_CONTROL_SX,
    id: idProp,
    placeholder = '',
    hideLabel = false,
    MenuProps: menuPropsProp = {},
    highlightedValues = [],
    floatingLabel = false,
  }) => {
    const generatedId = useId();
    const selectId = idProp ?? generatedId;
    const labelId = `${selectId}-label`;
    const formControlRef = useRef(null);
    const [menuPaperWidth, setMenuPaperWidth] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const optionLabelMap = useMemo(
      () => buildOptionLabelMap(filterOptions),
      [filterOptions]
    );

    const allOptionIds = useMemo(
      () => filterOptions.map(o => o.value),
      [filterOptions]
    );

    const selectedValues = useMemo(() => {
      if (!multiple) return value;
      return Array.isArray(value) ? value : [];
    }, [multiple, value]);

    const hasValue = multiple
      ? (selectedValues?.length ?? 0) > 0
      : value !== '' && value != null;

    const syncMenuWidth = useCallback(() => {
      if (formControlRef.current) {
        setMenuPaperWidth(formControlRef.current.offsetWidth);
      }
    }, []);

    const handleOpen = useCallback(() => {
      syncMenuWidth();
      if (floatingLabel) setIsOpen(true);
    }, [syncMenuWidth, floatingLabel]);

    const handleClose = useCallback(() => {
      if (floatingLabel) setIsOpen(false);
    }, [floatingLabel]);

    const handleSelectChange = useCallback(
      event => {
        const raw = event.target.value;
        if (multiple) {
          const next = resolveMultiSelectValue(
            selectedValues,
            raw,
            allOptionIds
          );
          handleChange(next);
          return;
        }
        handleChange(raw);
      },
      [multiple, selectedValues, allOptionIds, handleChange]
    );

    const getMultiDisplayLabels = useCallback(() => {
      const ids = selectedValues ?? [];
      if (isAllSelected(ids, allOptionIds)) {
        const allLabels = allOptionIds.map(
          id => optionLabelMap[id] ?? String(id)
        );
        return {
          display: allLabel,
          tooltip: allLabels.join(', '),
          forceTooltip: true,
        };
      }
      if (!ids.length) {
        const empty = emptyLabel || allLabel;
        return { display: empty, tooltip: empty };
      }
      const labels = ids.map(id => optionLabelMap[id] ?? String(id));
      return {
        display: labels.join(', '),
        tooltip: labels.join(', '),
      };
    }, [selectedValues, allOptionIds, allLabel, emptyLabel, optionLabelMap]);

    const renderDisplayValue = useCallback(
      selected => {
        if (floatingLabel && !hasValue) {
          return ' ';
        }

        if (multiple) {
          const { display, tooltip, forceTooltip } = getMultiDisplayLabels();
          return (
            <EllipsisTooltip
              title={tooltip}
              force={forceTooltip}
              style={{ display: 'block', width: '100%' }}
              {...dropdownTooltipProps}
            >
              {display}
            </EllipsisTooltip>
          );
        }

        if (selected === '' || selected == null) {
          if (placeholder) {
            return (
              <Box
                component="span"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {placeholder}
              </Box>
            );
          }
          return '\u00a0';
        }

        const resolved = optionLabelMap[selected] ?? String(selected);

        return (
          <EllipsisTooltip
            title={resolved}
            style={{ display: 'block', width: '100%' }}
            {...dropdownTooltipProps}
          >
            {resolved}
          </EllipsisTooltip>
        );
      },
      [
        floatingLabel,
        hasValue,
        multiple,
        getMultiDisplayLabels,
        optionLabelMap,
        placeholder,
      ]
    );

    const renderOptionLabel = useCallback(
      optionLabel => (
        <Box sx={menuItemLabelSx}>
          <EllipsisTooltip
            title={optionLabel}
            style={{ width: '100%', maxWidth: '100%' }}
            {...dropdownTooltipProps}
          >
            {optionLabel}
          </EllipsisTooltip>
        </Box>
      ),
      []
    );

    const renderMenuItems = useCallback(() => {
      const items = [];

      if (multiple && isAll) {
        const allChecked = isAllSelected(selectedValues, allOptionIds);
        items.push(
          <MenuItem
            key={ALL_OPTION_VALUE}
            value={ALL_OPTION_VALUE}
            aria-label={allLabel}
            sx={allChecked ? { color: DROPDOWN_ACTIVE_COLOR } : undefined}
          >
            <Checkbox
              checked={allChecked}
              tabIndex={-1}
              disableRipple
              sx={checkboxSx}
              inputProps={{ 'aria-hidden': true }}
            />
            {renderOptionLabel(allLabel)}
          </MenuItem>
        );
      }

      filterOptions.forEach(option => {
        const isSelected = multiple
          ? isOptionSelected(selectedValues, option.value)
          : highlightedValues.includes(option.value) || value === option.value;

        if (multiple) {
          items.push(
            <MenuItem
              key={String(option.value)}
              value={option.value}
              aria-label={option.label}
            >
              <Checkbox
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                sx={checkboxSx}
                inputProps={{ 'aria-hidden': true }}
              />
              {renderOptionLabel(option.label)}
            </MenuItem>
          );
          return;
        }

        items.push(
          <MenuItem
            key={String(option.value)}
            value={option.value}
            aria-label={option.label}
            sx={isSelected ? { color: DROPDOWN_ACTIVE_COLOR } : undefined}
          >
            {renderOptionLabel(option.label)}
          </MenuItem>
        );
      });

      return items;
    }, [
      multiple,
      isAll,
      allLabel,
      selectedValues,
      allOptionIds,
      filterOptions,
      value,
      highlightedValues,
      renderOptionLabel,
    ]);

    const mergedMenuProps = useMemo(() => {
      const {
        PaperProps: paperPropsFromProp = {},
        sx: menuSxFromProp,
        ...restMenuProps
      } = menuPropsProp;

      return {
        disableScrollLock: true,
        // Multi-select: use the plain 'menu' variant so the list doesn't
        // auto-scroll to the selected item — selecting "All" selects every
        // option, which the default 'selectedMenu' variant would jump to the end.
        ...(multiple ? { variant: 'menu' } : {}),
        ...restMenuProps,
        sx: {
          zIndex: 1300,
          ...(menuSxFromProp ?? {}),
        },
        PaperProps: {
          ...paperPropsFromProp,
          sx: {
            ...getMenuPaperSx(menuPaperWidth),
            ...paperPropsFromProp.sx,
          },
        },
      };
    }, [menuPropsProp, menuPaperWidth, multiple]);

    // Single-select: always shrink so the label sits in the outline notch (not inside the field).
    // Multi-select: shrink when showing emptyLabel, allLabel, or any selection.
    const labelShrink = hideLabel
      ? false
      : floatingLabel
        ? isOpen || hasValue
        : multiple
          ? Boolean(
              emptyLabel ||
              (isAll && allLabel) ||
              (selectedValues?.length ?? 0) > 0
            )
          : true;

    const outlinedInput = hideLabel ? (
      <OutlinedInput notched={false} />
    ) : (
      <OutlinedInput
        label={labelShrink ? label : undefined}
        notched={labelShrink}
      />
    );

    return (
      <FormControl
        ref={formControlRef}
        size={size}
        sx={{ ...DEFAULT_CONTROL_SX, ...sx }}
        disabled={disabled}
        error={error}
        fullWidth={
          sx?.minWidth === '100%' ||
          sx?.maxWidth === '100%' ||
          sx?.width === '100%'
        }
      >
        {!hideLabel && (
          <InputLabel id={labelId} shrink={labelShrink} required={required}>
            {label}
          </InputLabel>
        )}
        <Select
          id={selectId}
          labelId={hideLabel ? undefined : labelId}
          label={hideLabel ? undefined : label}
          aria-label={hideLabel ? label : undefined}
          multiple={multiple}
          value={multiple ? selectedValues : (value ?? '')}
          onChange={handleSelectChange}
          onOpen={handleOpen}
          onClose={handleClose}
          displayEmpty
          input={outlinedInput}
          renderValue={renderDisplayValue}
          MenuProps={mergedMenuProps}
          sx={selectDisplaySx}
        >
          {renderMenuItems()}
        </Select>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  }
);

export default CommonDropdown;
